import { useState } from "react";
import { ChevronRight, Clock, FileText, Shield, Link2, MapPin, Navigation, Phone, Mail, MessageCircle, Images, X as Close,
  ParkingCircle, ShowerHead, Utensils, Wifi, Toilet, Grid3x3, Moon, Lock, Dumbbell, HeartPulse, ShoppingBag, Snowflake, Check } from "lucide-react";
import { rm } from "@/lib/api";

const ICONS = { parking: ParkingCircle, shower: ShowerHead, restaurant: Utensils, cafe: Utensils, food: Utensils, wifi: Wifi, toilet: Toilet,
  grid: Grid3x3, surau: Moon, locker: Lock, gym: Dumbbell, aed: HeartPulse, firstaid: HeartPulse, shop: ShoppingBag, aircond: Snowflake };

export function Gallery({ photos, name }) {
  const [all, setAll] = useState(false);
  if (!photos.length) return null;
  const side = photos.slice(1, 5);
  return (
    <>
      <div className={`relative grid gap-3 ${side.length ? "md:grid-cols-2" : ""}`} data-testid="centre-gallery">
        <img src={photos[0]} alt={name} className="aspect-[16/10] w-full rounded-[1.5rem] object-cover md:aspect-auto md:h-[26rem]" />
        {!!side.length && <div className={`hidden h-[26rem] gap-3 md:grid ${side.length > 1 ? "grid-cols-2 grid-rows-[repeat(2,minmax(0,1fr))]" : "grid-rows-[minmax(0,1fr)]"}`}>
          {side.map((p, i) => <img key={p} src={p} alt={name} className={`h-full min-h-0 w-full rounded-2xl object-cover ${(side.length === 2 || (side.length === 3 && i === 0)) ? "row-span-2" : ""}`} />)}
        </div>}
        {photos.length > 1 && <button type="button" onClick={() => setAll(true)} className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-black shadow" data-testid="centre-all-photos"><Images className="h-4 w-4" />All photos ({photos.length})</button>}
      </div>
      {all && <div className="fixed inset-0 z-[80] overflow-y-auto bg-black/90 p-4" onClick={() => setAll(false)} data-testid="centre-photos-modal">
        <button type="button" className="fixed right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-white/15 text-white" aria-label="Close" data-testid="centre-photos-close"><Close className="h-5 w-5" /></button>
        <div className="mx-auto grid max-w-4xl gap-3 pt-12 sm:grid-cols-2">{photos.map((p) => <img key={p} src={p} alt={name} className="w-full rounded-2xl object-cover" />)}</div>
      </div>}
    </>
  );
}

const Row = ({ icon: I, label, sub, href, onClick, testid }) => {
  const body = <><I className="h-5 w-5 shrink-0 asv-accent" /><span className="min-w-0 flex-1"><span className="block font-semibold">{label}</span>{sub && <span className="asv-muted block text-xs">{sub}</span>}</span>{(href || onClick) && <ChevronRight className="asv-muted h-4 w-4 shrink-0" />}</>;
  const cls = "flex w-full items-center gap-3 border-b border-[color:var(--asv-line)] py-3.5 text-left text-sm last:border-0";
  return href ? <a href={href} target="_blank" rel="noreferrer" className={cls} data-testid={testid}>{body}</a>
    : onClick ? <button type="button" onClick={onClick} className={cls} data-testid={testid}>{body}</button> : <div className={cls} data-testid={testid}>{body}</div>;
};

export function InfoCard({ c }) {
  const [hours, setHours] = useState(false);
  const [contact, setContact] = useState(false);
  const wa = c.whatsapp && `https://wa.me/${c.whatsapp.replace(/\D/g, "").replace(/^0/, "60")}`;
  const hasContact = c.phone || c.email || wa || Object.keys(c.socials || {}).length;
  if (!c.operation_hours && !c.terms && !c.privacy && !hasContact) return null;
  return (
    <section className="asv-card p-5" data-testid="centre-info">
      <h2 className="asv-display text-xl font-extrabold">Information</h2>
      <div className="mt-2">
        {c.operation_hours && <Row icon={Clock} label="Opening hours" sub={hours ? c.operation_hours : null} onClick={() => setHours(!hours)} testid="centre-hours" />}
        {c.privacy && <Row icon={Shield} label={c.privacy.title || "Privacy Policy"} href={c.privacy.url} testid="centre-privacy" />}
        {c.terms && <Row icon={FileText} label={c.terms.title || "Terms & Conditions"} href={c.terms.url} testid="centre-terms" />}
        {hasContact && <Row icon={Link2} label="Get connected" onClick={() => setContact(!contact)} testid="centre-connect" />}
        {contact && <div className="space-y-3 pb-2 pl-8 pt-4 text-sm" data-testid="centre-contact-list">
          {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2"><Phone className="h-4 w-4 asv-accent" />{c.phone}</a>}
          {wa && <a href={wa} target="_blank" rel="noreferrer" className="flex items-center gap-2"><MessageCircle className="h-4 w-4 asv-accent" />WhatsApp</a>}
          {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 break-all"><Mail className="h-4 w-4 asv-accent" />{c.email}</a>}
          {Object.entries(c.socials || {}).map(([k, u]) => <a key={k} href={u} target="_blank" rel="noreferrer" className="flex items-center gap-2 capitalize"><Link2 className="h-4 w-4 asv-accent" />{k === "x" ? "X" : k}</a>)}
        </div>}
      </div>
    </section>
  );
}

export function FacilitiesCard({ c }) {
  const [more, setMore] = useState(false);
  const list = c.facilities || [];
  const nav = c.map_url || c.waze_url;
  if (!list.length && !nav) return null;
  const shown = more ? list : list.slice(0, 6);
  return (
    <div className="space-y-5">
      {!!list.length && <section className="asv-card p-5" data-testid="centre-facilities">
        <h2 className="asv-display text-xl font-extrabold">Facilities</h2>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3 text-sm">{shown.map((f) => { const I = ICONS[f.icon] || Check; return <span key={f.title} className="inline-flex items-center gap-2" title={f.text}><I className="h-4 w-4 asv-accent" />{f.title}</span>; })}</div>
        {list.length > 6 && <button type="button" onClick={() => setMore(!more)} className="mt-3 text-sm font-semibold underline" data-testid="centre-facilities-more">{more ? "View less" : "View more"}</button>}
      </section>}
      {nav && <section className="asv-card px-5 py-1" data-testid="centre-directions">
        {c.map_url && <Row icon={MapPin} label="Open in Google Maps" href={c.map_url} testid="centre-gmaps" />}
        {c.waze_url && <Row icon={Navigation} label="Open in Waze" href={c.waze_url} testid="centre-waze" />}
      </section>}
    </div>
  );
}

export function Memberships({ c }) {
  if (!c.plans?.length) return null;
  const link = c.site_url ? `${c.site_url}/membership` : null;
  const Wrap = link ? "a" : "div";
  return (
    <section data-testid="centre-memberships">
      <Wrap {...(link ? { href: link, target: "_blank", rel: "noreferrer" } : {})} className="asv-btn flex w-full justify-center rounded-2xl py-5 text-center text-base font-extrabold sm:text-lg" data-testid="centre-membership-banner">
        Join {c.name} Memberships to apply discounts!</Wrap>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{c.plans.map((p) => (
        <div key={p.name} className="asv-card p-4" data-testid="centre-plan"><p className="font-bold">{p.name}</p>
          <p className="asv-accent text-sm font-bold">{rm(p.price_sen)}{p.months ? ` · ${p.months} month${p.months === 1 ? "" : "s"}` : ""}</p>
          {p.summary && <p className="asv-muted mt-1 text-xs">{p.summary}</p>}</div>))}</div>
    </section>
  );
}
